import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet, apiPost } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const t = localStorage.getItem('auth_token');
      if (!t) { if (active) setLoading(false); return; }
      try {
        const u = await apiGet('/auth/me');
        if (active) setUser(u);
      } catch (e) {
        console.warn('Session restore failed — clearing token:', e?.message || e);
        localStorage.removeItem('auth_token');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const login = useCallback(async (user_id, pin) => {
    const data = await apiPost('/auth/login', { user_id, pin });
    localStorage.setItem('auth_token', data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await apiPost('/auth/logout', {}); }
    catch (e) { console.warn('Logout API failed (clearing local anyway):', e?.message || e); }
    localStorage.removeItem('auth_token');
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, isAuthenticated: !!user, setUser }),
    [user, loading, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
